import { useState } from "react";
import { useRecordContext, useTranslate } from "ra-core";
import { CreateButton } from "@/components/admin/create-button";
import { DataTable } from "@/components/admin/data-table";
import { ExportButton } from "@/components/admin/export-button";
import { List } from "@/components/admin/list";
import { SearchInput } from "@/components/admin/search-input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowRightLeft } from "lucide-react";
import { useCrmIdentity } from "@/hooks/useCrmIdentity";

import { TopToolbar } from "../layout/TopToolbar";
import { PortfolioReassignmentDialog } from "./PortfolioReassignmentDialog";
import { SalesDeleteButton } from "./SalesDeleteButton";

const SalesListActions = ({
  onOpenReassign,
}: {
  onOpenReassign?: () => void;
}) => {
  const { identity } = useCrmIdentity();
  const role = identity?.role || (identity?.administrator ? "admin" : "sales");
  const isAdmin = role === "superadmin" || role === "admin";

  return (
    <TopToolbar>
      {isAdmin && onOpenReassign && (
        <Button
          variant="outline"
          size="sm"
          onClick={onOpenReassign}
          className="flex items-center gap-1.5"
        >
          <ArrowRightLeft className="h-4 w-4" />
          Riassegna Portafoglio
        </Button>
      )}
      <ExportButton />
      <CreateButton label="resources.sales.action.new" />
    </TopToolbar>
  );
};

const filters = [<SearchInput source="q" alwaysOn />];

const RoleBadge = ({ role }: { role: string }) => {
  if (role === "superadmin") {
    return (
      <Badge
        variant="default"
        className="bg-purple-600 hover:bg-purple-700 text-white font-medium"
      >
        Superadmin
      </Badge>
    );
  }
  if (role === "admin") {
    return (
      <Badge
        variant="outline"
        className="border-blue-500 text-blue-600 dark:border-blue-400 dark:text-blue-400 font-medium"
      >
        Admin
      </Badge>
    );
  }
  return (
    <Badge variant="secondary" className="font-normal text-muted-foreground">
      Sales
    </Badge>
  );
};

const OptionsField = (_props: { label?: string | boolean }) => {
  const record = useRecordContext();
  const translate = useTranslate();
  if (!record) return null;
  const role = record.role || (record.administrator ? "admin" : "sales");

  return (
    <div className="flex flex-row gap-2 items-center">
      <RoleBadge role={role} />
      {record.disabled && (
        <Badge
          variant="outline"
          className="border-orange-400 text-orange-600 dark:border-orange-600 dark:text-orange-400"
        >
          {translate("resources.sales.fields.disabled", { _: "Disabilitato" })}
        </Badge>
      )}
    </div>
  );
};

export function SalesList() {
  const [reassignOpen, setReassignOpen] = useState(false);

  return (
    <>
      <List
        filters={filters}
        actions={
          <SalesListActions onOpenReassign={() => setReassignOpen(true)} />
        }
        sort={{ field: "first_name", order: "ASC" }}
      >
        <DataTable bulkActionButtons={false}>
          <DataTable.Col source="first_name" />
          <DataTable.Col source="last_name" />
          <DataTable.Col source="email" />
          <DataTable.Col label="Ruolo / Stato">
            <OptionsField />
          </DataTable.Col>
          <DataTable.Col label="Azioni" className="w-12 text-right">
            <SalesDeleteButton />
          </DataTable.Col>
        </DataTable>
      </List>
      <PortfolioReassignmentDialog
        open={reassignOpen}
        onOpenChange={setReassignOpen}
      />
    </>
  );
}
